import React from 'react';
import { connect } from 'react-redux';
import type { WCmsPageView } from '@zola/svc-web-api-ts-client';

// Components
import Toggle from 'react-toggle';
import { Link } from '@zola/zola-ui/src/components/Link';

// Actions
import { showModal } from 'actions/ModalActions';

import { AppDispatch } from 'reducers/index';
import { PAGE_TYPES_ENUM } from 'components/manage/website/util/pageTypes';
import EDIT_PAGES_CONTENT from './content';

type EditPageHeaderDispatchProps = {
  openTipsModal: (tips: React.ReactNode) => void;
};

type EditPageHeaderProps = EditPageHeaderDispatchProps & {
  page: WCmsPageView;
  pageDek?: string;
  themeKey: string;
  onPageToggle?: (pageId?: number) => void;
  onAdd?: () => void;
  dataTooltip?: string;
  // only used on RSVP
  isGotItTooltip?: boolean;
  gotItTooltip?: React.ReactNode;
};

const EditPageHeader: React.FC<EditPageHeaderProps> = ({
  page,
  pageDek,
  themeKey,
  onPageToggle,
  openTipsModal,
  isGotItTooltip,
  gotItTooltip,
  dataTooltip = 'Control if guests see this page when your website is visible. Set to "Inactive" to hide it. Set to "Active" if you\'re ready to share.',
}) => {
  const pageType = page?.type || '';
  const pageId = page?.id;
  const isPageHidden = page?.hidden;
  const isHomePage = pageType === PAGE_TYPES_ENUM.HOME;
  const seeDemoHref = `/wedding/sample-${themeKey}/${isHomePage ? '' : pageType?.toLowerCase()}`;
  // content
  const { title, infoBlock, dek: defaultDek, tips: tipsContent } = EDIT_PAGES_CONTENT[pageType];
  const dek = pageDek || defaultDek;

  return (
    <section className={`edit-page__header edit-${pageType.toLowerCase()}__header`}>
      <div className="edit-page__title-section">
        <h1 className="edit-page__title">{title}</h1>
        {!isHomePage && (
          <div
            className={`toggle-page-visibility no-tooltip-on-mobile with-tooltip ${
              isGotItTooltip ? 'hide-tooltip' : ''
            }`}
            data-tooltip={dataTooltip}
          >
            <Toggle
              checked={!isPageHidden}
              onChange={() => {
                if (onPageToggle) onPageToggle(pageId);
              }}
              icons={{
                checked: <span className="toggle-label checked">Active</span>,
                unchecked: <span className="toggle-label unchecked">Inactive</span>,
              }}
            />
            {gotItTooltip && isGotItTooltip && gotItTooltip}
          </div>
        )}
        {tipsContent && (
          <div className="edit-page__tips" onClick={() => openTipsModal(tipsContent)}>
            <i className="tips__icon zolaicon-tips" />
            <span className="tips__text">Tips</span>
          </div>
        )}
      </div>
      <div className="edit-page__intro">{dek}</div>
      {themeKey && (
        <div className="edit-page__see-demo web-to-reg__sample-page-link">
          <Link arrow href={seeDemoHref} target="_blank" rel="noopener noreferrer">
            See Sample Page
          </Link>
        </div>
      )}
      {infoBlock && <div className="edit-page__info-block">{infoBlock}</div>}
    </section>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch): EditPageHeaderDispatchProps => ({
  openTipsModal: tips => {
    dispatch(showModal('TIPS_MODAL', { tipsContent: tips }, { size: 'md' }));
  },
});

export default connect(null, mapDispatchToProps)(EditPageHeader);
